/**
 * Qobuz Playlist Import — frontend driver
 */

import {
  addQobuzPlaylistImportProgressListener,
  removeQobuzPlaylistImportProgressListener,
} from './qobuzPlaylistImportProgress';
import {
  authedFetch,
  clearQobuzImportFlag,
  getAuthToken,
  hasCompletedInitialQobuzImport,
} from './qobuzPlaylistImportCore';
import { runQobuzPlaylistImportLoop } from './qobuzPlaylistImportLoop';
import type { ImportMode, ImportProgress, ImportStatus } from './qobuzPlaylistImportTypes';

export type { ImportMode, ImportProgress, ImportStatus };
export { clearQobuzImportFlag, hasCompletedInitialQobuzImport };
export { formatPlaylistImportCompletionLine } from './qobuzPlaylistImportMessages';

let runningPromise: Promise<ImportProgress> | null = null;

export async function fetchQobuzImportStatus(): Promise<ImportStatus | null> {
  try {
    const res = await authedFetch('/api/user/playlists/import/qobuz/status', 'GET');
    if (!res.ok) return null;
    const data: any = await res.json();
    return data?.status ?? null;
  } catch {
    return null;
  }
}

export function isQobuzPlaylistImportInFlight(): boolean {
  return runningPromise != null;
}

interface RunOptions {
  mode?: ImportMode;
  onProgress?: (p: ImportProgress) => void;
}

export function runQobuzPlaylistImport(options: RunOptions = {}): Promise<ImportProgress> {
  if (options.onProgress) {
    addQobuzPlaylistImportProgressListener(options.onProgress);
  }

  if (runningPromise) {
    return runningPromise.finally(() => {
      if (options.onProgress) removeQobuzPlaylistImportProgressListener(options.onProgress);
    });
  }

  const mode: ImportMode = options.mode ?? 'initial';
  runningPromise = runQobuzPlaylistImportLoop(mode, options.onProgress).finally(() => {
    runningPromise = null;
    if (options.onProgress) removeQobuzPlaylistImportProgressListener(options.onProgress);
  });

  return runningPromise;
}

export function maybeAutoImportQobuzPlaylists(): void {
  if (hasCompletedInitialQobuzImport()) return;
  if (runningPromise) return;
  if (!getAuthToken()) return;
  runQobuzPlaylistImport({ mode: 'initial' }).catch(() => {});
}
